import React, { useCallback, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUpload } from '@fortawesome/free-solid-svg-icons'
import type { WheelImageUploadControls } from '../../types/GrowthWheelManagement'
import GWPlaceHolder from '../../assets/gw_placeholder.png'

const ACCEPT_MIME = new Set(['image/png', 'image/jpeg'])

function isAllowedWheelImageFile(file: File): boolean {
  if (ACCEPT_MIME.has(file.type)) return true
  return /\.(png|jpe?g)$/i.test(file.name)
}

type Props = {
  wheelImageUrl?: string | null
  roleName?: string
  wheelImageUpload?: WheelImageUploadControls
}

export function WheelManagementImageCard({
  wheelImageUrl,
  roleName,
  wheelImageUpload,
}: Props) {
  const [localError, setLocalError] = useState<string | null>(null)

  const runUpload = useCallback(
    (file: File) => {
      if (!wheelImageUpload || wheelImageUpload.isUploading) return
      if (!isAllowedWheelImageFile(file)) {
        setLocalError('Only PNG and JPG images are allowed.')
        return
      }
      setLocalError(null)
      wheelImageUpload.onFileSelected(file)
    },
    [wheelImageUpload],
  )

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (file) runUpload(file)
  }

  const clientRejectMessage =
    localError || wheelImageUpload?.errorMessage

  return (
    <div className="gw-wm__card">
      <h2 className="gw-wm__card-title">Growth Wheel Image</h2>

      <div className="gw-wm__image-layout">
        <div
          className={`gw-wm__image-preview-box${
            wheelImageUpload?.isUploading ? ' gw-wm__image-preview-box--busy' : ''
          }`}
        >
          <img
            src={wheelImageUrl || GWPlaceHolder}
            alt={`Growth wheel${roleName ? ` for ${roleName}` : ''}`}
            className="gw-wm__img-fit"
          />
          {wheelImageUpload && (
            <div className="gw-wm__image-replace-overlay">
              {wheelImageUpload.isUploading ? (
                <span className="gw-wm__image-uploading-text">Uploading…</span>
              ) : (
                <label className="gw-wm__image-replace-label">
                  <input
                    type="file"
                    className="gw-wm__file-input-hidden"
                    accept="image/png,image/jpeg,.png,.jpg,.jpeg"
                    aria-label="Replace growth wheel image"
                    disabled={wheelImageUpload.isUploading}
                    onChange={onInputChange}
                  />
                  <span className="gw-wm__image-replace-chip">
                    <FontAwesomeIcon icon={faUpload} className="gw-wm__image-replace-icon" />
                    <span className="gw-wm__image-replace-text">Replace Image</span>
                  </span>
                </label>
              )}
            </div>
          )}
        </div>

        <div className="gw-wm__image-meta">
          <p className="gw-wm__image-copy-desc mb-2">
            This image appears on the Growth Wheel landing page for this role.
            Upload a new image to replace the current one.
          </p>
          <p className="gw-wm__image-copy-hint mb-0">
            Recommended: Square image, minimum 800x800px, PNG or JPG format
          </p>
          {clientRejectMessage && !wheelImageUpload?.isUploading && (
            <p className="gw-wm__upload-error gw-wm__upload-error--image-meta mb-0">
              {clientRejectMessage}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
